import React, { useCallback, useState } from 'react';
import { IUserJson } from '@scale/shared/types/user';
import { Typography, Spinner, TextField, Button, Accordion, Markdown } from '@scale/scaleui';
import API from '@scale/frontend-lib/dashboard-api';
import { ScreeningExplainMetadata } from '@scale/shared/taskerScreening/types';

interface TaskerScreeningAuditorExplainProps {
  user: IUserJson;
}

const TaskerScreeningAuditorExplain: React.FC<TaskerScreeningAuditorExplainProps> = () => {
  const [userIdOrEmail, setUserIdOrEmail] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [messages, setMessages] = useState<ScreeningExplainMetadata[]>([]);

  const fetchExplain = async (userIdOrEmail: string) => {
    const { explainMetadata } = await API.genai.fetchPendingScreening({
      explainUser: userIdOrEmail,
    });
    setMessages(explainMetadata ?? []);
  };

  const handleFetchExplain = useCallback(async () => {
    if (!userIdOrEmail) return;
    setLoading(true);
    setMessages([]);
    await fetchExplain(userIdOrEmail);
    setLoading(false);
  }, [userIdOrEmail]);

  return (
    <div className="scaleui scale-theme scale-tailwind p-4">
      <div>
        <Typography.H4>Auditor Explain</Typography.H4>
        <p>
          Think of this tool as Corp Explain. Use this tool to debug why an auditor sees what they
          see in the Tasker Screening Grading table. Use this tool to debug benchmark walls,
          benchmark accuracy thresholds, benchmark disabling, permissions, etc.
        </p>
        <div className="mt-4">
          <TextField
            className="w-[400px]"
            id="userIdOrEmail"
            type="text"
            value={userIdOrEmail}
            onChange={e => setUserIdOrEmail(e.target.value)}
            placeholder="User ID or Email"
          />
          <Button
            className="mt-4"
            variant="primary"
            disabled={!userIdOrEmail || loading}
            onClick={handleFetchExplain}
          >
            Explain Auditor
          </Button>
          {messages.length > 0 && (
            <div className="mt-4 w-full">
              <Typography.H4>Explanation</Typography.H4>
              {messages.map(message => (
                <div className="mt-4" key={message.title}>
                  <Accordion
                    header={message.title}
                    tags={[
                      {
                        value: message.timestamp,
                        variant: message.title.includes('ERROR') ? 'danger' : 'info',
                      },
                    ]}
                    target={<Markdown markdown={message.message} />}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="mt-4 flex flex-row">
          {loading && (
            <div>
              <Spinner size="sm" />
              <div className="mt-4">
                <Typography.Paragraph2>This may take a few seconds.</Typography.Paragraph2>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskerScreeningAuditorExplain;
